// ── Prompt builder — Ask AI about this verse ─────────────────────────────────
// All logic is pure, client-side, zero-cost. No fetch, no server, no API keys.
//
// Public API:
//   buildPrompt(options)  → { prompt, tier, charCount }
//   buildProviderUrl(provider, prompt) → string | null
//
// Edge cases handled internally (callers never need to pre-process):
//   EC-1  No runningSummary     — placeholder injected; first-verse framing
//   EC-3  Theme / verseType absent — 'unknown'/'general'; extra anti-hallucination bullet
//   EC-4  Prev/next verse absent — those lines omitted entirely (not labelled)
//   EC-5  quranicAllusionScore absent/zero — score=0 + note to only mention if clearly present
//   EC-6  Unknown provider      — buildProviderUrl returns null; caller falls back to copy
//
// Length tiers (prompt chars, before URL-encoding):
//   Tier 1 ≤ 4 000   — full prompt: memory + prev/next verse context
//   Tier 2 ≤ 5 500   — truncate prev/next verse to 60 chars each
//   Tier 3 ≤ 7 000   — drop prev/next entirely; truncate running summary to 400 chars
//   Tier 4  > 7 000  — minimal fallback prompt (always fits)

/** @import { PoemSession } from './poem-session-types.js' */

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const TIER1_LIMIT = 4_000;
const TIER2_LIMIT = 5_500;
const TIER3_LIMIT = 7_000;

// Overview prompt tiers (chars before URL-encoding):
//   OV-Tier 1 ≤ 5 000  — full poem text
//   OV-Tier 2 ≤ 7 500  — poem text truncated to 3 000 chars
//   OV-Tier 3  > 7 500 — minimal fallback (metadata only + first 1 500 chars of poem)
const OV_TIER1_LIMIT = 5_000;
const OV_TIER2_LIMIT = 7_500;
const OV_VERSES_FULL_LEN   = 4_000;
const OV_VERSES_SHORT_LEN  = 3_000;
const OV_VERSES_MINIMAL_LEN = 1_500;

const SUMMARY_TRUNCATE_LEN   = 400;
const CONTEXT_TRUNCATE_LEN   = 60;

const PROVIDER_URLS = {
  claude:  'https://claude.ai/new?q=',
  chatgpt: 'https://chat.openai.com/?q=',
  copilot: 'https://copilot.microsoft.com/?q=',
  gemini:  'https://gemini.google.com/app?q=',
};

// ─────────────────────────────────────────────────────────────────────────────
// Template: English only
// ─────────────────────────────────────────────────────────────────────────────

export const TMPL_EN = `\
You are analysing a verse from classical Arabic poetry. Read every instruction \
below before writing a single word of your response.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POEM CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Title          {{POEM_TITLE}}
Poet           {{POET}}
Era            {{ERA}}
Metre (بحر)   {{METER}}
Theme          {{THEME}}
Verse type     {{VERSE_TYPE}}
Poet signature {{POET_SIGNATURE}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POEM MEMORY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
{{RUNNING_SUMMARY}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MANDATORY ANTI-HALLUCINATION CONSTRAINTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• All interpretation MUST align with the theme {{THEME}} and verse type {{VERSE_TYPE}}.{{THEME_NOTE}}
• Do NOT invent themes, emotions, or contexts not supported by the text.
• Interpret the verse in a way consistent with the poet's known signature style: {{POET_SIGNATURE}}.
• Return ONE complete, self-contained analysis. Do NOT ask follow-up questions.
• On Qur'anic allusions: if uncertain, say so explicitly — do not assert an allusion without clear evidence in the text.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
THE VERSE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Position in poem: {{VERSE_POSITION}}

Previous verse:  {{PREVIOUS_VERSE}}

▶ THIS VERSE:   {{VERSE}}

Next verse:      {{NEXT_VERSE}}

Qur'anic allusion score: {{QURANIC_SCORE}} (scale 0–1; ≥ 0.5 = this poet's corpus frequently engages Qur'anic imagery — check this verse carefully)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ANALYSIS — ALL SEVEN SECTIONS REQUIRED
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. PLAIN MEANING
Translate and paraphrase the verse in clear, accessible English. No jargon.

2. CONTEXT WITHIN THE POEM
How does this verse connect to what came before and after? What structural or emotional role does it play?

3. BALĀGHA & RHETORIC
Identify and explain: تشبيه (simile), استعارة (metaphor), طباق (antithesis), جناس (paronomasia), كناية (allusive expression), مجاز (figurative language), and any sonic or structural patterns. Explain the effect of each device found.

4. BEAUTY & EMOTIONAL IMPACT
What makes this verse striking or memorable? Identify the exact language choices that create the effect, and describe the emotional register they evoke.

5. WORD-LEVEL ANALYSIS & EVERYDAY USAGE
{{WORD}}
Choose 2–4 key Arabic words. For each: give the 3-letter root (جذر), grammatical role (نحو), classical meaning, and how the word appears in modern everyday Arabic.

6. QUR'ANIC & HADITH ALLUSIONS
Using the allusion score ({{QURANIC_SCORE}}) and your stylistic reading: identify any Qur'anic or hadith echoes, quote the source text, and explain how the allusion layers meaning onto the verse. If you find no allusion, state that clearly.

7. INTEGRATION WITH RUNNING SUMMARY
Explain precisely how this verse advances — or complicates — the poem's argument, emotion, or narrative arc as described in the running summary above.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SUMMARY TO PASTE BACK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
After completing the analysis, write a 3–5 sentence cumulative summary of the poem so far, updated to include this verse. Format it exactly as:

Summary to Paste Back:
[Your 3–5 sentences here]`;

// ─────────────────────────────────────────────────────────────────────────────
// Template: Arabic only
// ─────────────────────────────────────────────────────────────────────────────

export const TMPL_AR = `\
أنتَ تُحلِّل بيتًا من الشعر العربي الكلاسيكي. اقرأ جميع التعليمات الواردة أدناه قبل أن تكتب كلمةً واحدة من ردّك.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
سياق القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
عنوان القصيدة:  {{POEM_TITLE}}
الشاعر:         {{POET}}
العصر:          {{ERA}}
البحر الشعري:   {{METER}}
الموضوع:        {{THEME}}
نوع البيت:      {{VERSE_TYPE}}
توقيع الشاعر:   {{POET_SIGNATURE}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ذاكرة القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
{{RUNNING_SUMMARY}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
قيود منع الاختلاق — إلزامية
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• يجب أن ينسجم التحليل تمامًا مع موضوع القصيدة {{THEME}} ونوع البيت {{VERSE_TYPE}}.{{THEME_NOTE}}
• لا تخترع موضوعات أو مشاعر أو سياقات غير موجودة في النص.
• فسّر البيت بما يتوافق مع الأسلوب المعروف للشاعر: {{POET_SIGNATURE}}.
• قدّم تحليلًا كاملًا ومكتفيًا بذاته في ردٍّ واحد. لا تطرح أسئلة متابعة.
• في ما يتعلق بالإشارات القرآنية: إن كنت غير متأكد، صرّح بذلك — لا تُثبت إشارةً دون دليل واضح في النص.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
البيت المطلوب تحليله
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
موضع البيت: {{VERSE_POSITION}}

البيت السابق:   {{PREVIOUS_VERSE}}

▶ هذا البيت:   {{VERSE}}

البيت التالي:  {{NEXT_VERSE}}

درجة الإشارة القرآنية: {{QURANIC_SCORE}} (المقياس 0–1؛ الدرجة ≥ 0.5 تعني أن مجموعة هذا الشاعر كثيراً ما تتضمن إشارات قرآنية — تحقق من هذا البيت بعناية)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
التحليل — سبعة أقسام كاملة مطلوبة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

١. المعنى المباشر
اشرح معنى البيت بلغة عربية واضحة وبسيطة. تجنّب المصطلحات المعقدة.

٢. السياق داخل القصيدة
كيف يرتبط هذا البيت بما قبله وما بعده؟ ما دوره البنائي أو الوجداني في مسار القصيدة؟

٣. البلاغة والأساليب البيانية
حدّد وفسّر: تشبيه، استعارة، طباق، جناس، كناية، مجاز، وأي أنماط صوتية أو بنيوية. اشرح أثر كل أسلوب وُجد في البيت.

٤. الجمال والأثر الوجداني
ما الذي يجعل هذا البيت مؤثرًا أو راسخًا في الذاكرة؟ حدّد الاختيارات اللغوية الدقيقة التي تصنع هذا الأثر وصف المشاعر التي تثيرها.

٥. التحليل المعجمي والاستخدام اليومي
{{WORD}}
اختر 2–4 كلمات مفتاحية. لكل كلمة: الجذر الثلاثي، الدور النحوي، المعنى الكلاسيكي، وكيف تظهر الكلمة في العربية اليومية الحديثة.

٦. الإشارات القرآنية والحديثية
بناءً على درجة الإشارة ({{QURANIC_SCORE}}) وقراءتك الأسلوبية: هل يحتوي البيت على أصداء قرآنية أو حديثية؟ اقتبس النص المصدر ووضّح كيف تُضفي الإشارة طبقاتٍ من المعنى على البيت. وإن لم تجد إشارة، صرّح بذلك صراحةً.

٧. الربط بالملخص التراكمي
اشرح كيف يُقدِّم هذا البيتُ — أو يُعقِّد — حجةَ القصيدة أو مشاعرها أو مسارها السردي كما وُصف في الملخص التراكمي أعلاه.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ملخص للصق
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
بعد إتمام التحليل، اكتب ملخصًا تراكميًا من 3–5 جمل للقصيدة حتى الآن، مُحدَّثًا ليشمل هذا البيت. نسّقه بالضبط على النحو التالي:

ملخص للصق:
[3–5 جمل هنا]`;

// ─────────────────────────────────────────────────────────────────────────────
// Template: Bilingual (English + Arabic in parallel)
// ─────────────────────────────────────────────────────────────────────────────

export const TMPL_BILINGUAL = `\
You are analysing a verse from classical Arabic poetry. Respond to EVERY section \
in BOTH English and Arabic. Read every instruction below before writing.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POEM CONTEXT / سياق القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Title / العنوان:        {{POEM_TITLE}}
Poet / الشاعر:          {{POET}}
Era / العصر:            {{ERA}}
Metre / البحر:          {{METER}}
Theme / الموضوع:        {{THEME}}
Verse type / نوع البيت: {{VERSE_TYPE}}
Poet signature / التوقيع: {{POET_SIGNATURE}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POEM MEMORY / ذاكرة القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
{{RUNNING_SUMMARY}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MANDATORY ANTI-HALLUCINATION CONSTRAINTS / قيود منع الاختلاق — إلزامية
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• All interpretation MUST align with the theme {{THEME}} and verse type {{VERSE_TYPE}}.{{THEME_NOTE}}
  يجب أن ينسجم التحليل تمامًا مع موضوع القصيدة {{THEME}} ونوع البيت {{VERSE_TYPE}}.

• Do NOT invent themes, emotions, or contexts not supported by the text.
  لا تخترع موضوعات أو مشاعر أو سياقات غير موجودة في النص.

• Interpret the verse in a way consistent with the poet's known signature style: {{POET_SIGNATURE}}.
  فسّر البيت بما يتوافق مع الأسلوب المعروف للشاعر: {{POET_SIGNATURE}}.

• Return ONE complete, self-contained analysis. Do NOT ask follow-up questions.
  قدّم تحليلًا كاملًا ومكتفيًا بذاته في ردٍّ واحد. لا تطرح أسئلة متابعة.

• On Qur'anic allusions: if uncertain, say so explicitly.
  في ما يتعلق بالإشارات القرآنية: إن كنت غير متأكد، صرّح بذلك صراحةً.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
THE VERSE / البيت
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Position / موضع البيت: {{VERSE_POSITION}}

Previous verse / البيت السابق:  {{PREVIOUS_VERSE}}

▶ THIS VERSE / هذا البيت:       {{VERSE}}

Next verse / البيت التالي:      {{NEXT_VERSE}}

Qur'anic allusion score / درجة الإشارة القرآنية: {{QURANIC_SCORE}} (0–1; ≥ 0.5 = corpus-level tendency toward Qur'anic imagery)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ANALYSIS — ALL 7 SECTIONS, EACH IN ENGLISH THEN ARABIC
التحليل — سبعة أقسام، كل قسم بالإنجليزية ثم العربية
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. PLAIN MEANING / المعنى المباشر
[EN] Translate and paraphrase in clear, accessible English.
[AR] اشرح معنى البيت بلغة عربية واضحة وبسيطة.

2. CONTEXT WITHIN THE POEM / السياق داخل القصيدة
[EN] How does this verse connect to what came before and after? What role does it play?
[AR] كيف يرتبط هذا البيت بما قبله وما بعده؟ ما دوره البنائي أو الوجداني؟

3. BALĀGHA & RHETORIC / البلاغة والأساليب البيانية
[EN] Identify: simile (تشبيه), metaphor (استعارة), antithesis (طباق), paronomasia (جناس), allusion (كناية), figurative (مجاز), and sonic/structural patterns. Explain the effect of each.
[AR] حدّد: تشبيه، استعارة، طباق، جناس، كناية، مجاز، وأنماط صوتية أو بنيوية. اشرح أثر كل أسلوب.

4. BEAUTY & EMOTIONAL IMPACT / الجمال والأثر الوجداني
[EN] What makes this verse striking? Name the exact language choices and their emotional effect.
[AR] ما الذي يجعل هذا البيت مؤثرًا؟ حدّد الاختيارات اللغوية الدقيقة وأثرها الوجداني.

5. WORD-LEVEL ANALYSIS & EVERYDAY USAGE / التحليل المعجمي والاستخدام اليومي
{{WORD}}
[EN] Choose 2–4 key words: root (جذر), grammatical role, classical meaning, modern everyday usage.
[AR] اختر 2–4 كلمات مفتاحية: الجذر الثلاثي، الدور النحوي، المعنى الكلاسيكي، الاستخدام اليومي الحديث.

6. QUR'ANIC & HADITH ALLUSIONS / الإشارات القرآنية والحديثية
[EN] Score: {{QURANIC_SCORE}}. Identify allusions, quote source, explain layered meaning. If none, say so.
[AR] الدرجة: {{QURANIC_SCORE}}. حدّد الإشارات، اقتبس المصدر، وضّح الطبقات. وإن لم توجد، صرّح بذلك.

7. INTEGRATION WITH RUNNING SUMMARY / الربط بالملخص التراكمي
[EN] How does this verse advance the poem's argument, emotion, or narrative from the running summary?
[AR] كيف يُقدِّم هذا البيتُ حجةَ القصيدة أو مشاعرها أو مسارها من حيث توقفنا في الملخص؟

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SUMMARY TO PASTE BACK / ملخص للصق
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Write a 3–5 sentence cumulative summary updated with this verse, in BOTH languages:

Summary to Paste Back:
[English: 3–5 sentences]

ملخص للصق:
[العربية: 3–5 جمل]`;

// ─────────────────────────────────────────────────────────────────────────────
// Minimal fallback (Tier 4) — always fits regardless of verse or summary length
// ─────────────────────────────────────────────────────────────────────────────

const TMPL_FALLBACK_EN = `\
Analyse this verse from classical Arabic poetry.

Poem: "{{POEM_TITLE}}" by {{POET}} ({{ERA}})
Theme: {{THEME}} | Metre: {{METER}} | Style: {{POET_SIGNATURE}}{{THEME_NOTE}}

▶ Verse:
{{VERSE}}
Position: {{VERSE_POSITION}}

Provide all of the following:
1. Plain meaning in English
2. Key rhetorical devices (balāgha)
3. Emotional impact and what makes it beautiful
4. Analysis of 2–3 key Arabic words (root, modern usage)
5. Any Qur'anic allusion (score: {{QURANIC_SCORE}}) — quote source if found

End your response with exactly:

Summary to Paste Back:
[3–5 sentences summarising the poem up to this verse]`;

const TMPL_FALLBACK_AR = `\
حلّل هذا البيت من الشعر العربي الكلاسيكي.

القصيدة: "{{POEM_TITLE}}" لـ {{POET}} ({{ERA}})
الموضوع: {{THEME}} | البحر: {{METER}} | الأسلوب: {{POET_SIGNATURE}}{{THEME_NOTE}}

▶ البيت:
{{VERSE}}
الموضع: {{VERSE_POSITION}}

قدّم جميع ما يلي:
١. المعنى المباشر بلغة عربية واضحة
٢. الأساليب البلاغية الرئيسية
٣. الأثر الوجداني وما يجعله جميلًا
٤. تحليل 2–3 كلمات مفتاحية (الجذر، الاستخدام الحديث)
٥. أي إشارة قرآنية (الدرجة: {{QURANIC_SCORE}}) — اقتبس المصدر إن وُجد

اختم ردّك بالضبط بما يلي:

ملخص للصق:
[3–5 جمل تلخص القصيدة حتى هذا البيت]`;

const TMPL_FALLBACK_BILINGUAL = `\
Analyse this verse from classical Arabic poetry. Respond in BOTH English and Arabic.

Poem: "{{POEM_TITLE}}" by {{POET}} ({{ERA}})
Theme: {{THEME}} | Metre: {{METER}} | Style: {{POET_SIGNATURE}}{{THEME_NOTE}}

▶ Verse / البيت:
{{VERSE}}
Position / الموضع: {{VERSE_POSITION}}

Cover: plain meaning · key balāgha devices · emotional impact · 2–3 word analyses (root, modern usage) · Qur'anic allusion check (score: {{QURANIC_SCORE}})

End with:

Summary to Paste Back:
[English: 3–5 sentences]

ملخص للصق:
[العربية: 3–5 جمل]`;

// ─────────────────────────────────────────────────────────────────────────────
// Verse position label
// ─────────────────────────────────────────────────────────────────────────────

function getVersePositionLabel(verseIdx, totalVerses, isAr) {
  const n     = (typeof verseIdx    === 'number' && verseIdx    >= 0) ? verseIdx + 1 : 1;
  const total = (typeof totalVerses === 'number' && totalVerses >  0) ? totalVerses  : 0;
  if (n === 1) {
    return isAr
      ? `المطلع (البيت الأول${total ? ` من ${total}` : ''}) — يضع الإطار العاطفي والموضوعي للقصيدة`
      : `Opening verse (مطلع — verse 1${total ? ` of ${total}` : ''}) — sets the emotional and thematic register; treat as the poet's opening declaration`;
  }
  if (total > 0 && n === total) {
    return isAr
      ? `الخاتمة (البيت ${n} من ${total}) — تأمّل كيف يُغلق هذا البيتُ القصيدة أو يُصدّى لمطلعها`
      : `Closing verse (خاتمة — verse ${n} of ${total}) — consider how this verse resolves or echoes the poem's opening`;
  }
  return isAr
    ? `البيت ${n}${total ? ` من ${total}` : ''}`
    : `Verse ${n}${total ? ` of ${total}` : ''}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Input sanitisation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitises any user-controlled text before inserting it into a prompt template.
 *
 * - Trims leading/trailing whitespace
 * - Normalises runs of spaces/tabs to a single space (preserves newlines)
 * - Collapses 3+ consecutive newlines to 2 (single blank line max)
 * - Removes ASCII control characters below 0x20 (keeps \t, \n, \r)
 * - Strips {{ and }} to prevent injection of fake template placeholders
 * - Collapses long ━ divider sequences that could spoof section headers
 * - Truncates to maxLen characters
 *
 * Arabic Unicode, harakat, and all standard punctuation are preserved.
 *
 * @param {string} str
 * @param {number} [maxLen=2000]
 * @returns {string}
 */
export function sanitiseVerseInput(str, maxLen = 2_000) {
  if (typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[^\S\n]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/\{\{|\}\}/g, '')
    .replace(/━{5,}/g, '———')
    .slice(0, maxLen);
}

// ─────────────────────────────────────────────────────────────────────────────
// Template filling
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Replaces every {{KEY}} in the template with the corresponding value.
 * Unrecognised keys are left as-is (visible in the prompt — safe).
 *
 * @param {string} tmpl
 * @param {Record<string, string>} vars
 * @returns {string}
 */
export function fillTemplate(tmpl, vars) {
  return tmpl.replace(/\{\{([A-Z_]+)\}\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider URL builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the redirect URL for the given AI provider with the prompt pre-filled.
 * Returns null when the provider is unrecognised — callers should fall back to
 * "Copy prompt" behaviour in the UI (EC-6).
 *
 * @param {'claude'|'chatgpt'|'copilot'} provider
 * @param {string} prompt
 * @returns {string|null}
 */
export function buildProviderUrl(provider, prompt) {
  const base = PROVIDER_URLS[provider];
  if (!base) return null;
  return base + encodeURIComponent(prompt);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main entry point
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Assembles a pre-filled AI analysis prompt from verse and poem data.
 *
 * All inputs are sanitised internally — callers do not need to pre-sanitise.
 * Edge cases (missing theme, absent context verses, zero score) are handled here.
 * Provider URL construction is intentionally separate (use buildProviderUrl).
 *
 * @param {Object}  options
 * @param {string}  options.poemId                 — used for logging only; not injected into template
 * @param {string}  options.verseId                — used for logging only; not injected into template
 * @param {string}  options.verseText              — Arabic verse (verse.textAr)
 * @param {string}  [options.poemTitle]            — poem.titleAr
 * @param {string}  [options.poetName]             — poem.poetAr
 * @param {string}  [options.era]                  — poem.era
 * @param {string}  [options.meter]                — poem.meter
 * @param {string}  [options.theme]                — poem.topic
 * @param {string}  [options.verseType]            — genre/form; defaults to 'general' when absent
 * @param {number}  [options.quranicAllusionScore] — 0–1 float; defaults to 0 when absent/NaN
 * @param {string}  [options.poetSignature]        — short attribution, e.g. "المتنبي (ق. 10م)"
 * @param {string}  [options.previousVerseText]    — omitted from prompt when empty (EC-4)
 * @param {string}  [options.nextVerseText]        — omitted from prompt when empty (EC-4)
 * @param {string}  [options.runningSummary]       — PoemSession.runningSummary; may be empty (EC-1)
 * @param {number}  [options.verseIdx]             — 0-based index; used for مطلع / خاتمة labelling
 * @param {number}  [options.totalVerses]          — total verses in poem; 0 if unknown
 * @param {string}  [options.tappedWord]           — word the user selected (optional focus)
 * @param {'en'|'ar'|'both'} [options.languageMode] — output language; default "en"
 *
 * @returns {{ prompt: string, tier: 1|2|3|4, charCount: number }}
 */
export function buildPrompt(options) {
  const {
    verseText            = '',
    poemTitle            = '',
    poetName             = '',
    era                  = '',
    meter                = '',
    theme                = '',
    verseType            = '',
    quranicAllusionScore = 0,
    poetSignature        = '',
    previousVerseText,
    nextVerseText,
    runningSummary       = '',
    verseIdx             = 0,
    totalVerses          = 0,
    tappedWord,
    languageMode         = 'en',
  } = options ?? {};

  const lang  = languageMode;
  const isAr  = lang === 'ar';
  const isBoth = lang === 'both';

  // ── Fallback labels ────────────────────────────────────────────────────────
  const NONE_SUM  = isAr
    ? '(لا يوجد ملخص بعد — هذا أول تحليل)'
    : '(No summary yet — this is the first analysis)';
  const OMITTED   = isAr ? '(محذوف)' : '(omitted for length)';
  const WORD_NONE = isAr
    ? 'اختر الكلمات الأكثر أهمية من البيت.'
    : 'Choose the most significant words from the verse.';

  // ── EC-3: missing theme / verseType ───────────────────────────────────────
  const themeIsUnknown  = !theme?.trim();
  const resolvedTheme   = themeIsUnknown
    ? (isAr ? 'غير معروف' : 'unknown')
    : sanitiseVerseInput(theme, 150);
  const resolvedVerseType = !verseType?.trim()
    ? (isAr ? 'عام' : 'general')
    : sanitiseVerseInput(verseType, 100);
  const THEME_NOTE = themeIsUnknown
    ? (isAr
        ? '\n• الموضوع غير معروف — تجنّب الادعاء بموضوع محدد للقصيدة.'
        : '\n• Theme is unknown — avoid strong thematic claims.')
    : '';

  // ── EC-5: missing / zero Qur'anic allusion score ──────────────────────────
  const resolvedScore = (typeof quranicAllusionScore === 'number' && !isNaN(quranicAllusionScore))
    ? quranicAllusionScore
    : 0;
  const scoreNote = resolvedScore === 0
    ? (isAr
        ? ' (صفر — اذكر الإشارة فقط إن وجدت دليلًا نصيًا صريحًا)'
        : ' (zero — only note allusion if you find clear textual evidence)')
    : '';

  // ── Base safe vars (shared across all tiers) ──────────────────────────────
  const safe = {
    POEM_TITLE:     sanitiseVerseInput(poemTitle  || (isAr ? '(بلا عنوان)' : '(untitled)'), 200),
    POET:           sanitiseVerseInput(poetName   || (isAr ? '(مجهول)'      : '(unknown)'),  150),
    ERA:            sanitiseVerseInput(era        || (isAr ? '(غير معروف)'  : '(unknown)'),  100),
    METER:          sanitiseVerseInput(meter      || (isAr ? '(غير معروف)'  : '(unknown)'),  100),
    THEME:          resolvedTheme,
    VERSE_TYPE:     resolvedVerseType,
    THEME_NOTE,
    POET_SIGNATURE: sanitiseVerseInput(poetSignature || poetName || (isAr ? '(غير معروف)' : '(unknown)'), 200),
    VERSE:          sanitiseVerseInput(verseText, 500),
    VERSE_POSITION: getVersePositionLabel(verseIdx, totalVerses, isAr),
    QURANIC_SCORE:  resolvedScore.toFixed(2) + scoreNote,
    WORD: tappedWord
      ? sanitiseVerseInput(isAr
          ? `الكلمة المختارة للتحليل التفصيلي: ${tappedWord}`
          : `Focus word for detailed analysis: ${tappedWord}`, 200)
      : WORD_NONE,
  };

  // ── EC-4: absent context verses — '' causes the line to be cleaned up ─────
  const prevSafe = previousVerseText?.trim() ? sanitiseVerseInput(previousVerseText, 500) : '';
  const nextSafe = nextVerseText?.trim()     ? sanitiseVerseInput(nextVerseText, 500)     : '';

  const tmplFull     = isBoth ? TMPL_BILINGUAL : isAr ? TMPL_AR     : TMPL_EN;
  const tmplFallback = isBoth ? TMPL_FALLBACK_BILINGUAL
                     : isAr  ? TMPL_FALLBACK_AR
                     :         TMPL_FALLBACK_EN;

  // ── Tier 1: full prompt ────────────────────────────────────────────────────
  const varsT1 = {
    ...safe,
    PREVIOUS_VERSE:  prevSafe,
    NEXT_VERSE:      nextSafe,
    RUNNING_SUMMARY: sanitiseVerseInput(runningSummary || NONE_SUM, 2_000),
  };
  let prompt = cleanEmptyContextLines(fillTemplate(tmplFull, varsT1));
  if (prompt.length <= TIER1_LIMIT) return finish(prompt, 1);

  // ── Tier 2: truncate prev/next verse ──────────────────────────────────────
  const varsT2 = {
    ...safe,
    PREVIOUS_VERSE:  prevSafe ? truncate(prevSafe, CONTEXT_TRUNCATE_LEN) : '',
    NEXT_VERSE:      nextSafe ? truncate(nextSafe, CONTEXT_TRUNCATE_LEN) : '',
    RUNNING_SUMMARY: sanitiseVerseInput(runningSummary || NONE_SUM, 2_000),
  };
  prompt = cleanEmptyContextLines(fillTemplate(tmplFull, varsT2));
  if (prompt.length <= TIER2_LIMIT) return finish(prompt, 2);

  // ── Tier 3: drop prev/next; truncate running summary ──────────────────────
  const varsT3 = {
    ...safe,
    PREVIOUS_VERSE:  OMITTED,
    NEXT_VERSE:      OMITTED,
    RUNNING_SUMMARY: runningSummary
      ? truncate(sanitiseVerseInput(runningSummary, 2_000), SUMMARY_TRUNCATE_LEN)
      : NONE_SUM,
  };
  prompt = fillTemplate(tmplFull, varsT3);
  if (prompt.length <= TIER3_LIMIT) return finish(prompt, 3);

  // ── Tier 4: minimal fallback — always fits ─────────────────────────────────
  prompt = fillTemplate(tmplFallback, safe);
  return finish(prompt, 4);
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/** @param {string} str @param {number} maxLen @returns {string} */
function truncate(str, maxLen) {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen).trimEnd() + '…';
}

/**
 * Removes context-verse lines that resolved to empty after template filling.
 * Applied at Tier 1 and Tier 2; at Tier 3 the OMITTED label is used instead.
 *
 * @param {string} prompt
 * @returns {string}
 */
function cleanEmptyContextLines(prompt) {
  return prompt
    .replace(/^Previous verse[^:\n]*:\s*$/gm, '')
    .replace(/^Next verse[^:\n]*:\s*$/gm, '')
    .replace(/^البيت السابق[^:\n]*:\s*$/gm, '')
    .replace(/^البيت التالي[^:\n]*:\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n');
}

/**
 * @param {string} prompt
 * @param {1|2|3|4} tier
 * @returns {{ prompt: string, tier: 1|2|3|4, charCount: number }}
 */
function finish(prompt, tier) {
  return { prompt, tier, charCount: prompt.length };
}

// ─────────────────────────────────────────────────────────────────────────────
// Overview templates (Prompt 1 — Poem Overview — One-Time Generation)
// ─────────────────────────────────────────────────────────────────────────────

const TMPL_OVERVIEW_EN = `\
You are writing a reader's guide entry for a classical Arabic poem.
Read the complete poem and every instruction below before writing a single word.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POEM METADATA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Title             {{POEM_TITLE}}
Poet              {{POET}}
Era               {{ERA}}
Metre (بحر)      {{METER}}
Poem type         {{VERSE_TYPE}}
Theme(s)          {{THEME}}
Poet signature    {{POET_SIGNATURE}}
{{HISTORICAL_CONTEXT_LINE}}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FULL POEM TEXT (all abyāt)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
{{ALL_VERSES}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MANDATORY CONSTRAINTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• POET section: draw on your knowledge of this poet and era. If uncertain about specific details, say so clearly — do not invent biographical information.
• All other sections: base your answer only on what is present in the poem text above.
• Do NOT invent themes, emotions, or events not evidenced by the text.
• Do NOT ask follow-up questions — one complete, self-contained response only.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TASK — POEM OVERVIEW
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Write a structured overview for a reader who is new to classical Arabic poetry.
Cover all five sections below in the order given. Do not skip any section or any bayt.

Produce ONLY the following output, formatted exactly as shown:

Poem Overview to Paste Back:

POET
[2–3 sentences: who this poet was, their era and region, what they specialised in (panegyric, elegy, love poetry, philosophy, etc.), and how they were received by critics and later readers. If your knowledge is limited, say so.]

HISTORICAL & CULTURAL CONTEXT
[2–3 sentences: situate this poem in its Islamic era — what period of Islamic history is this, what was happening in society politically or intellectually, who were key contemporaries (poets, rulers, scholars), and what cultural currents shaped this poet's world.]

THEMATIC OVERVIEW
[2–3 sentences: the central theme(s), what the poet is trying to communicate or achieve, and what the reader should pay attention to as they move through it. Note the classical structure (e.g. nasīb → raḥīl → gharaḍ) if this is a multi-section qaṣīda.]

PLAIN READING
Bayt 1: [plain English paraphrase — what this bayt says in simple, accessible language. No literary analysis — just clear meaning.]
Bayt 2: [plain English paraphrase]
...continue for every bayt in order, do not skip any...
Bayt N: [plain English paraphrase]

SUMMARY TO PASTE BACK
[3–5 sentences covering: the overall themes, the poet's goal, the central message of the poem, and what a reader should carry forward when analysing individual verses in depth.]`;

const TMPL_OVERVIEW_AR = `\
أنتَ تكتب مقدمةً قارئيةً لقصيدة عربية كلاسيكية.
اقرأ القصيدة كاملةً وكل تعليمات أدناه قبل أن تكتب كلمةً واحدة.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
معلومات القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
عنوان القصيدة:  {{POEM_TITLE}}
الشاعر:         {{POET}}
العصر:          {{ERA}}
البحر الشعري:   {{METER}}
نوع القصيدة:    {{VERSE_TYPE}}
الموضوع:        {{THEME}}
توقيع الشاعر:   {{POET_SIGNATURE}}
{{HISTORICAL_CONTEXT_LINE}}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
نص القصيدة كاملاً (جميع الأبيات)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
{{ALL_VERSES}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
قيود إلزامية
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• قسم الشاعر: استعن بمعرفتك بهذا الشاعر وعصره. إن كنت غير متأكد من تفاصيل بعينها، فصرّح بذلك — لا تخترع معلومات سيرة ذاتية.
• سائر الأقسام: استند فقط إلى ما هو موجود في نص القصيدة أعلاه.
• لا تخترع موضوعات أو مشاعر أو أحداثاً غير موجودة في النص.
• لا تطرح أسئلة متابعة — ردٌّ واحد مكتمل ومكتفٍ بذاته فحسب.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
المهمة — نظرة عامة على القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
اكتب نظرةً عامةً منظّمةً لقارئ جديد على الشعر العربي الكلاسيكي.
غطِّ الأقسام الخمسة أدناه بالترتيب المحدد. لا تتجاهل أي قسم أو أي بيت.

أنتج فقط المخرج التالي، منسّقًا بالضبط كما هو موضح:

نظرة عامة للصق:

الشاعر
[2–3 جمل: مَن كان هذا الشاعر، عصره وإقليمه، ما تخصّص فيه (مديح، رثاء، غزل، حكمة، فلسفة...)، وكيف تلقّى النقادُ والأجيالُ اللاحقة شعرَه. إن كانت معرفتك محدودة، فأشر إلى ذلك.]

السياق التاريخي والحضاري
[2–3 جمل: ضَع هذه القصيدة في سياقها الإسلامي — ما الحقبة التاريخية الإسلامية؟ ما الذي كان يجري في المجتمع على الصعيد السياسي أو الفكري؟ مَن هم أبرز المعاصرين من شعراء وحكام وعلماء؟ وما التيارات الثقافية التي شكّلت عالم هذا الشاعر؟]

النظرة الموضوعية
[2–3 جمل: الموضوع أو الموضوعات المحورية للقصيدة، ما يسعى الشاعر إلى توصيله أو تحقيقه، وما يجب أن يُولِيَه القارئ اهتمامًا خلال قراءته. أشر إلى البنية الكلاسيكية (كالنسيب → الرحيل → الغرض) إن كانت هذه قصيدةً متعددة الأقسام.]

القراءة التأسيسية
البيت 1: [معنى هذا البيت بلغة عربية بسيطة وسلسة — لا تحليل أدبي، مجرد المعنى الواضح.]
البيت 2: [معنى البيت بلغة بسيطة]
...واصل لكل بيت بالترتيب، لا تتجاهل أيًا...
البيت N: [معنى البيت بلغة بسيطة]

ملخص للصق
[3–5 جمل تشمل: الموضوعات العامة، وهدف الشاعر، والرسالة المحورية للقصيدة، وما ينبغي للقارئ أن يحمله معه عند تحليل الأبيات بشكل مفصّل.]`;

const TMPL_OVERVIEW_BILINGUAL = `\
You are writing a reader's guide entry for a classical Arabic poem.
Respond in BOTH English and Arabic for each section.
Read the complete poem and every instruction below before writing a single word.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POEM METADATA / معلومات القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Title / العنوان:        {{POEM_TITLE}}
Poet / الشاعر:          {{POET}}
Era / العصر:            {{ERA}}
Metre / البحر:          {{METER}}
Poem type / النوع:      {{VERSE_TYPE}}
Theme(s) / الموضوع:     {{THEME}}
Poet signature / التوقيع: {{POET_SIGNATURE}}
{{HISTORICAL_CONTEXT_LINE}}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FULL POEM TEXT / نص القصيدة كاملاً
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
{{ALL_VERSES}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MANDATORY CONSTRAINTS / قيود إلزامية
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• POET section: draw on your knowledge of this poet. If uncertain, say so — do not invent biographical details.
  قسم الشاعر: استعن بمعرفتك. إن كنت غير متأكد، صرّح بذلك — لا تخترع معلومات.
• All other sections: base your answer only on the poem text above.
  سائر الأقسام: استند فقط إلى نص القصيدة أعلاه.
• Do NOT invent themes or events not evidenced by the text.
  لا تخترع موضوعات أو أحداثاً غير موجودة في النص.
• Do NOT ask follow-up questions — one complete response only.
  لا تطرح أسئلة متابعة — ردٌّ واحد مكتمل فحسب.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TASK — POEM OVERVIEW / المهمة — نظرة عامة على القصيدة
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Write a structured overview covering all five sections below.
For each section write English first, then Arabic. Do not skip any section or any bayt.

Produce ONLY the following output, formatted exactly as shown:

Poem Overview to Paste Back:

POET / الشاعر
EN: [2–3 sentences: who this poet was, era, specialisation, critical reception. If uncertain, say so.]
AR: [2–3 جمل بالعربية]

HISTORICAL & CULTURAL CONTEXT / السياق التاريخي والحضاري
EN: [2–3 sentences: Islamic era, what was happening in society politically or intellectually, key contemporaries (poets, rulers, scholars), cultural currents shaping this poet's world.]
AR: [2–3 جمل بالعربية]

THEMATIC OVERVIEW / النظرة الموضوعية
EN: [2–3 sentences: central themes, poet's aim, what to pay attention to. Note classical structure if relevant.]
AR: [2–3 جمل بالعربية]

PLAIN READING / القراءة التأسيسية
Bayt 1 — EN: [plain English paraphrase] | AR: [معنى البيت بالعربية البسيطة]
Bayt 2 — EN: [plain English paraphrase] | AR: [معنى البيت]
...continue for every bayt in order, do not skip any...
Bayt N — EN: [plain English paraphrase] | AR: [معنى البيت]

SUMMARY TO PASTE BACK / ملخص للصق
EN: [3–5 sentences: overall themes, poet's goal, central message, what to carry forward for verse analysis.]
AR: [3–5 جمل بالعربية]`;

// Minimal fallback (OV-Tier 3) — always fits regardless of poem length
const TMPL_OVERVIEW_FALLBACK_EN = `\
Give a structured reader's guide overview of this classical Arabic poem.

Poem: "{{POEM_TITLE}}" by {{POET}} ({{ERA}})
Type: {{VERSE_TYPE}} | Theme: {{THEME}} | Metre: {{METER}} | Style: {{POET_SIGNATURE}}
{{HISTORICAL_CONTEXT_LINE}}
Opening abyāt (poem too long for full text):
{{ALL_VERSES}}

Produce ONLY:

Poem Overview to Paste Back:

POET
[2–3 sentences: who this poet was, specialisation, critical reception. If uncertain, say so.]

HISTORICAL & CULTURAL CONTEXT
[2–3 sentences: Islamic era, what was happening in society politically or intellectually, key contemporaries, cultural currents shaping this poet's world.]

THEMATIC OVERVIEW
[2–3 sentences: central themes, poet's aim, what to pay attention to. Note classical structure if relevant.]

PLAIN READING
[Based on the opening abyāt above — plain English paraphrase per bayt shown.]
Bayt 1: [plain English paraphrase]
...continue for every bayt shown above, do not skip any...
Bayt N: [plain English paraphrase]

SUMMARY TO PASTE BACK
[3–5 sentences: overall themes, poet's goal, central message, what to carry forward.]`;

const TMPL_OVERVIEW_FALLBACK_AR = `\
قدّم نظرةً عامةً منظّمةً لهذه القصيدة العربية الكلاسيكية.

القصيدة: "{{POEM_TITLE}}" لـ {{POET}} ({{ERA}})
النوع: {{VERSE_TYPE}} | الموضوع: {{THEME}} | البحر: {{METER}} | الأسلوب: {{POET_SIGNATURE}}
{{HISTORICAL_CONTEXT_LINE}}
مطلع الأبيات (القصيدة طويلة جداً للنص الكامل):
{{ALL_VERSES}}

أنتج فقط:

نظرة عامة للصق:

الشاعر
[2–3 جمل: مَن كان هذا الشاعر، تخصّصه، تلقّي النقاد لشعره. إن كنت غير متأكد، أشر إلى ذلك.]

السياق التاريخي والحضاري
[2–3 جمل: العصر الإسلامي، ما كان يجري في المجتمع سياسياً أو فكرياً، أبرز المعاصرين من شعراء وحكام وعلماء، والتيارات الثقافية التي شكّلت عالم هذا الشاعر.]

النظرة الموضوعية
[2–3 جمل: الموضوعات المحورية، هدف الشاعر، ما يجب على القارئ مراقبته. أشر إلى البنية الكلاسيكية إن وُجدت.]

القراءة التأسيسية
[استناداً إلى مطلع الأبيات أعلاه — معنى كل بيت بلغة بسيطة.]
البيت 1: [المعنى بلغة بسيطة]
...واصل لكل بيت مُدرَج أعلاه، لا تتجاهل أيًا...
البيت N: [المعنى بلغة بسيطة]

ملخص للصق
[3–5 جمل: الموضوعات العامة، هدف الشاعر، الرسالة المحورية، ما ينبغي حمله عند التحليل التفصيلي.]`;

const TMPL_OVERVIEW_FALLBACK_BILINGUAL = `\
Give a structured reader's guide overview of this classical Arabic poem. Respond in BOTH English and Arabic.

Poem: "{{POEM_TITLE}}" by {{POET}} ({{ERA}})
Type: {{VERSE_TYPE}} | Theme: {{THEME}} | Metre: {{METER}} | Style: {{POET_SIGNATURE}}
{{HISTORICAL_CONTEXT_LINE}}
Opening abyāt (poem too long for full text):
{{ALL_VERSES}}

Produce ONLY:

Poem Overview to Paste Back:

POET / الشاعر
EN: [2–3 sentences] | AR: [2–3 جمل]

HISTORICAL & CULTURAL CONTEXT / السياق التاريخي والحضاري
EN: [2–3 sentences: Islamic era, society, contemporaries, cultural currents.] | AR: [2–3 جمل]

THEMATIC OVERVIEW / النظرة الموضوعية
EN: [2–3 sentences] | AR: [2–3 جمل]

PLAIN READING / القراءة التأسيسية
Bayt 1 — EN: [paraphrase] | AR: [معنى]
...continue for every bayt shown, do not skip any...
Bayt N — EN: [paraphrase] | AR: [معنى]

SUMMARY TO PASTE BACK / ملخص للصق
EN: [3–5 sentences] | AR: [3–5 جمل]`;

// ─────────────────────────────────────────────────────────────────────────────
// buildOverviewPrompt — Prompt 1 (Poem Overview — One-Time Generation)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Assembles the poem overview prompt from full poem text and metadata.
 *
 * All inputs are sanitised internally. Edge cases (missing theme/type,
 * long poem text) are handled via tier compression.
 *
 * @param {Object} options
 * @param {string}   options.allVersesText       — All abyāt joined with newlines
 * @param {string}   [options.poemTitle]
 * @param {string}   [options.poetName]
 * @param {string}   [options.era]
 * @param {string}   [options.meter]
 * @param {string}   [options.theme]
 * @param {string}   [options.verseType]
 * @param {string}   [options.poetSignature]
 * @param {string}   [options.historicalContext] — Optional historical context; omitted when empty
 * @param {'en'|'ar'|'both'} [options.languageMode]
 *
 * @returns {{ prompt: string, tier: 1|2|3, charCount: number }}
 */
export function buildOverviewPrompt(options) {
  const {
    allVersesText    = '',
    poemTitle        = '',
    poetName         = '',
    era              = '',
    meter            = '',
    theme            = '',
    verseType        = '',
    poetSignature    = '',
    historicalContext = '',
    languageMode     = 'en',
  } = options ?? {};

  const lang  = languageMode;
  const isAr  = lang === 'ar';
  const isBoth = lang === 'both';

  const resolvedTheme     = sanitiseVerseInput(theme     || (isAr ? 'غير معروف' : 'unknown'), 150);
  const resolvedVerseType = sanitiseVerseInput(verseType || (isAr ? 'عام'        : 'general'), 100);

  const ctxRaw = sanitiseVerseInput(historicalContext, 500);
  const HISTORICAL_CONTEXT_LINE = ctxRaw
    ? (isAr
        ? `السياق التاريخي: ${ctxRaw}`
        : `Historical context: ${ctxRaw}`)
    : '';

  const safe = {
    POEM_TITLE:             sanitiseVerseInput(poemTitle   || (isAr ? '(بلا عنوان)' : '(untitled)'), 200),
    POET:                   sanitiseVerseInput(poetName    || (isAr ? '(مجهول)'      : '(unknown)'),  150),
    ERA:                    sanitiseVerseInput(era         || (isAr ? '(غير معروف)'  : '(unknown)'),  100),
    METER:                  sanitiseVerseInput(meter       || (isAr ? '(غير معروف)'  : '(unknown)'),  100),
    THEME:                  resolvedTheme,
    VERSE_TYPE:             resolvedVerseType,
    POET_SIGNATURE:         sanitiseVerseInput(poetSignature || poetName || (isAr ? '(غير معروف)' : '(unknown)'), 200),
    HISTORICAL_CONTEXT_LINE,
  };

  const tmplFull     = isBoth ? TMPL_OVERVIEW_BILINGUAL : isAr ? TMPL_OVERVIEW_AR     : TMPL_OVERVIEW_EN;
  const tmplFallback = isBoth ? TMPL_OVERVIEW_FALLBACK_BILINGUAL
                     : isAr  ? TMPL_OVERVIEW_FALLBACK_AR
                     :         TMPL_OVERVIEW_FALLBACK_EN;

  const versesFull    = sanitiseVerseInput(allVersesText, OV_VERSES_FULL_LEN);
  const versesShort   = truncate(sanitiseVerseInput(allVersesText, OV_VERSES_FULL_LEN), OV_VERSES_SHORT_LEN);
  const versesMinimal = truncate(sanitiseVerseInput(allVersesText, OV_VERSES_FULL_LEN), OV_VERSES_MINIMAL_LEN);

  // OV-Tier 1: full poem text
  let prompt = cleanHistoricalContextLine(fillTemplate(tmplFull, { ...safe, ALL_VERSES: versesFull }));
  if (prompt.length <= OV_TIER1_LIMIT) return { prompt, tier: 1, charCount: prompt.length };

  // OV-Tier 2: poem text truncated
  prompt = cleanHistoricalContextLine(fillTemplate(tmplFull, { ...safe, ALL_VERSES: versesShort }));
  if (prompt.length <= OV_TIER2_LIMIT) return { prompt, tier: 2, charCount: prompt.length };

  // OV-Tier 3: minimal fallback
  prompt = cleanHistoricalContextLine(fillTemplate(tmplFallback, { ...safe, ALL_VERSES: versesMinimal }));
  return { prompt, tier: 3, charCount: prompt.length };
}

/**
 * Removes the historical context line when it resolved to empty.
 * @param {string} prompt
 * @returns {string}
 */
function cleanHistoricalContextLine(prompt) {
  return prompt
    .replace(/^Historical context:\s*$/gm, '')
    .replace(/^السياق التاريخي:\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n');
}
